import { AGE_MINIMUM, EARLIEST_BIRTH_YEAR } from '../constants/ageGate.constants'

export interface DateOfBirthParts {
  day: string
  month: string
  year: string
}

export type DateOfBirthValidation =
  | { ok: true; isoDate: string }
  | { ok: false; reason: 'incomplete' | 'impossible' | 'future' | 'too_old' }

const pad2 = (value: number) => String(value).padStart(2, '0')

/**
 * Validates the three input fields and builds a `YYYY-MM-DD` string.
 *
 * The ISO string is assembled from the parts, never from a local `Date`, so the user's
 * timezone cannot shift the submitted day. Mirrors the server's parseBirthDate rules.
 */
export function validateDateOfBirthParts(
  parts: DateOfBirthParts,
  now: Date = new Date()
): DateOfBirthValidation {
  const day = parts.day.trim()
  const month = parts.month.trim()
  const year = parts.year.trim()
  if (!/^\d{1,2}$/.test(day) || !/^\d{1,2}$/.test(month) || !/^\d{4}$/.test(year)) {
    return { ok: false, reason: 'incomplete' }
  }

  const d = Number(day)
  const m = Number(month)
  const y = Number(year)
  const candidate = new Date(Date.UTC(y, m - 1, d))
  if (
    candidate.getUTCFullYear() !== y ||
    candidate.getUTCMonth() !== m - 1 ||
    candidate.getUTCDate() !== d
  ) {
    return { ok: false, reason: 'impossible' }
  }
  if (y < EARLIEST_BIRTH_YEAR) return { ok: false, reason: 'too_old' }

  const todayUtc = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())
  if (candidate.getTime() > todayUtc) return { ok: false, reason: 'future' }

  return { ok: true, isoDate: `${y}-${pad2(m)}-${pad2(d)}` }
}

/** Whole years by calendar date; a birthday today counts as completed. */
export function computeAgeYears(isoDate: string, now: Date = new Date()): number {
  const [y, m, d] = isoDate.split('-').map(Number)
  let age = now.getFullYear() - y
  const nowMonth = now.getMonth() + 1
  if (nowMonth < m || (nowMonth === m && now.getDate() < d)) age -= 1
  return age
}

export function isBelowMinimumAge(isoDate: string, now: Date = new Date()): boolean {
  return computeAgeYears(isoDate, now) < AGE_MINIMUM
}

/**
 * Language the confirmation date is spelled in. The app is English-only for now, so this is fixed
 * rather than the device language (a Hebrew phone showed "14 ביולי 1990" in an English dialog).
 * Change here when the app is localised (specs/coin-compliance AC-1.9).
 */
export const DATE_OF_BIRTH_CONFIRMATION_LOCALE = 'en-GB'

/**
 * Spells the date out ("14 July 2012") for the confirmation step.
 * Built from the ISO parts as UTC so no timezone can shift the day.
 */
export function formatDateOfBirthForConfirmation(
  isoDate: string,
  locale: string = DATE_OF_BIRTH_CONFIRMATION_LOCALE
): string {
  const [y, m, d] = isoDate.split('-').map(Number)
  const date = new Date(Date.UTC(y, m - 1, d))
  try {
    return new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(date)
  } catch {
    return `${d}/${m}/${y}`
  }
}
